import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CustomBackdrop } from '../../components';
import { useNotification } from '../../hooks/notifications/use-notification';
import { useNotificationsOperations } from '../../hooks/notifications/use-notifications-operations';
import { NotificationInvitationType, NotificationTypeEnum } from '../../types/notification';
import InvitationAccepted from './kinds/invitation-accepted/invitation-accepted';
import InvitationReceived from './kinds/invitation-received/invitation-received';
import NotificationDetailsCommon from './kinds/notification-details-common/notification-details-common';
import NotificationNotFound from './kinds/notification-not-found/notification-not-found';

const NotificationDetails: React.FC = () => {
  const { t } = useTranslation('notifications');
  const { correlationId } = useParams<{ correlationId: string }>();
  const { notification, loading } = useNotification(correlationId);
  const { markAsDisplayed } = useNotificationsOperations();

  useEffect(() => {
    if (correlationId) markAsDisplayed(correlationId);
  }, []);

  if (loading) return <CustomBackdrop />;

  switch (notification?.data.type) {
    case NotificationTypeEnum.OTHER: {
      return (
        <NotificationDetailsCommon
          title={notification.data.title}
          message={notification.data.message}
          createdTimestampUTC={notification.createdTimestampUTC}
        />
      );
    }
    case NotificationTypeEnum.INVITATION:
      switch (notification.data.invitationType) {
        case NotificationInvitationType.InvitationReceived:
          return <InvitationReceived notificationData={notification.data} />;
        case NotificationInvitationType.InvitationAcceptedByInvitee:
          return <InvitationAccepted notification={notification} />;
        case NotificationInvitationType.InvitationRejectedByInvitee:
          return (
            <NotificationDetailsCommon
              title={t('invitationRejectedByInvitee.title')}
              message={t('invitationRejectedByInvitee.message', {
                installationName: notification.data.installationName,
                inviteeName: notification.data.userName,
              })}
              createdTimestampUTC={notification.createdTimestampUTC}
            />
          );
        case NotificationInvitationType.InvitationCanceledByCreator:
          return (
            <NotificationDetailsCommon
              title={t('InvitationCanceledByCreator.title')}
              message={t('InvitationCanceledByCreator.message', {
                installationName: notification.data.installationName,
                creatorName: notification.data.userName,
              })}
              createdTimestampUTC={notification.createdTimestampUTC}
            />
          );
        default:
          return <NotificationNotFound />;
      }
    default: {
      return <NotificationNotFound />;
    }
  }
};

export default NotificationDetails;
